/* eslint-disable import/first */
import React from "react";
import ReactDOM from "react-dom";
import "./i18n";
import "./index.scss";
import App from "./App";

import { Buffer } from "buffer";

if (typeof window !== "undefined") {
    // Some bundled third-party scripts still check/use `process` in browser.
    if (!(window as any).process) {
        (window as any).process = {
            env: {
                NODE_ENV: "development",
                PUBLIC_URL: "",
            },
            nextTick: (cb: any) => setTimeout(cb, 0),
            browser: true,
        };
    }
    // Polyfill Buffer for Webpack 5
    (window as any).Buffer = Buffer;

    // HOOPS can transiently emit post_draw while OffscreenCanvas has 0x0 size during sheet/view transitions.
    // Guard drawImage to skip only this invalid state instead of crashing the app.
    const drawImagePatchFlag = "__cmic_drawImage_offscreen_zero_guard__";
    const proto = CanvasRenderingContext2D?.prototype as any;
    if (proto && !proto[drawImagePatchFlag]) {
        const originalDrawImage = proto.drawImage;
        proto.drawImage = function patchedDrawImage(...args: any[]) {
            const source = args[0] as any;
            const isOffscreen =
                typeof OffscreenCanvas !== "undefined" &&
                source instanceof OffscreenCanvas;
            if (isOffscreen && (source.width <= 0 || source.height <= 0)) {
                return;
            }
            try {
                return originalDrawImage.apply(this, args);
            } catch (error: any) {
                const isInvalidOffscreenState =
                    error?.name === "InvalidStateError" &&
                    typeof error?.message === "string" &&
                    error.message.includes("OffscreenCanvas") &&
                    error.message.includes("width or height of 0");
                if (isInvalidOffscreenState) {
                    return;
                }
                throw error;
            }
        };
        proto[drawImagePatchFlag] = true;
    }

    // HOOPS may reject async work during teardown/startup race with
    // "member called after shutdown". Ignore only this known transient error.
    const hoopsShutdownGuardFlag = "__cmic_hoops_shutdown_error_guard__";
    if (!(window as any)[hoopsShutdownGuardFlag]) {
        const isHoopsShutdownRace = (
            message: string,
            stack?: string
        ): boolean => {
            const msg = (message || "").toLowerCase();
            const st = (stack || "").toLowerCase();
            const hasShutdownMsg =
                msg.includes("member called after shutdown") ||
                st.includes("member called after shutdown");
            const hasHoopsTrace =
                st.includes("hoops-web-viewer") ||
                st.includes("setallowhighdpi") ||
                st.includes("onresize");
            // Some production/minified traces lose HOOPS symbols, but this message is emitted
            // by HOOPS teardown races we already guard elsewhere.
            return (
                hasShutdownMsg &&
                (hasHoopsTrace || st.length === 0 || st.includes("bundle.js"))
            );
        };

        window.addEventListener(
            "unhandledrejection",
            (event: PromiseRejectionEvent) => {
                const reason: any = event.reason;
                const message = String(reason?.message || reason || "");
                const stack =
                    typeof reason?.stack === "string" ? reason.stack : "";
                if (isHoopsShutdownRace(message, stack)) {
                    event.preventDefault();
                    event.stopImmediatePropagation();
                }
            },
            true
        );

        window.addEventListener(
            "error",
            (event: ErrorEvent) => {
                const message = String(event.message || "");
                const stack =
                    typeof event.error?.stack === "string"
                        ? event.error.stack
                        : "";
                if (isHoopsShutdownRace(message, stack)) {
                    event.preventDefault();
                    event.stopImmediatePropagation();
                }
            },
            true
        );

        const previousOnError = window.onerror;
        window.onerror = function onWindowError(
            message,
            source,
            lineno,
            colno,
            error
        ) {
            const msg = String(message || "");
            const stack =
                typeof (error as any)?.stack === "string"
                    ? (error as any).stack
                    : "";
            if (isHoopsShutdownRace(msg, stack)) {
                return true;
            }
            if (typeof previousOnError === "function") {
                return previousOnError.call(
                    window,
                    message,
                    source,
                    lineno,
                    colno,
                    error
                );
            }
            return false;
        };

        (window as any)[hoopsShutdownGuardFlag] = true;
    }
}

ReactDOM.render(<App />, document.getElementById("root"));
